import CustomDrawer from "@/components/drawer";
import { ActionIcon, Button, Group, Stack, Text } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconCircleMinus, IconCirclePlus } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { faNumber } from "./format";
import classes from "./home.module.scss";
import { HomeSection, MAX_SHORTCUTS, sectionGroups } from "./sections";

type Props = {
  opened: boolean;
  onClose: () => void;
  available: HomeSection[];
  selected: HomeSection[];
  onSave: (ids: string[]) => void;
  onReset: () => void;
};

const groupLabel = (section: HomeSection) =>
  sectionGroups.find((group) => group.id === section.group)?.label.manager ?? "";

export default function ShortcutEditor({ opened, onClose, available, selected, onSave, onReset }: Props) {
  const [draft, setDraft] = useState<string[]>([]);
  useEffect(() => {
    if (opened) setDraft(selected.map((section) => section.id));
  }, [opened, selected]);

  const byId = useMemo(() => new Map(available.map((section) => [section.id, section])), [available]);
  const picked = draft.map((id) => byId.get(id)).filter((section): section is HomeSection => Boolean(section));
  const rest = available.filter((section) => !draft.includes(section.id));
  const full = draft.length >= MAX_SHORTCUTS;

  const move = (index: number, step: number) =>
    setDraft((ids) => {
      const target = index + step;
      if (target < 0 || target >= ids.length) return ids;
      const next = [...ids];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const remove = (id: string) => setDraft((ids) => ids.filter((item) => item !== id));
  const add = (id: string) => setDraft((ids) => (ids.length >= MAX_SHORTCUTS ? ids : [...ids, id]));

  // The drawer keeps a history entry while open; going back closes it like the back button does.
  const finish = (apply: () => void) => {
    apply();
    window.history.back();
  };

  return (
    <CustomDrawer
      opened={opened}
      onClose={onClose}
      close={onClose}
      custom="shortcuts"
      position="bottom"
      size="92%"
      title={<Text fw={800}>ویرایش میانبرها</Text>}
    >
      <Stack gap="lg">
        <p className={classes.editorIntro}>
          تا {faNumber(MAX_SHORTCUTS)} بخش پرکاربردتان را برای صفحه‌ی خانه انتخاب کنید. ترتیب را با دکمه‌های بالا و پایین عوض کنید. «همه‌ی بخش‌ها» همیشه آخرین میانبر است.
        </p>

        <Stack gap={8}>
          <div className={classes.editorHead}>
            <h3 className={classes.editorTitle}>میانبرهای خانه</h3>
            <span className={classes.editorCount}>
              {faNumber(draft.length)} از {faNumber(MAX_SHORTCUTS)}
            </span>
          </div>
          <div className={classes.card}>
            {picked.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="lg">
                هنوز میانبری انتخاب نکرده‌اید.
              </Text>
            )}
            {picked.map((section, index) => {
              const Icon = section.icon;
              return (
                <div key={section.id} className={classes.editorRow}>
                  <span className={`${classes.editorIcon} ${classes.tone}`} data-tone={section.group}>
                    <Icon size={22} stroke={1.7} aria-hidden />
                  </span>
                  <span className={classes.editorLabel}>
                    <strong>{section.label}</strong>
                  </span>
                  <ActionIcon variant="subtle" color="gray" size={44} radius="xl" disabled={index === 0} onClick={() => move(index, -1)} aria-label={`بالا بردن «${section.label}»`}>
                    <IconArrowUp size={20} stroke={1.7} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="gray" size={44} radius="xl" disabled={index === picked.length - 1} onClick={() => move(index, 1)} aria-label={`پایین بردن «${section.label}»`}>
                    <IconArrowDown size={20} stroke={1.7} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" size={44} radius="xl" onClick={() => remove(section.id)} aria-label={`حذف «${section.label}» از میانبرها`}>
                    <IconCircleMinus size={22} stroke={1.7} />
                  </ActionIcon>
                </div>
              );
            })}
          </div>
        </Stack>

        {rest.length > 0 && (
          <Stack gap={8}>
            <div className={classes.editorHead}>
              <h3 className={classes.editorTitle}>بخش‌های دیگر</h3>
            </div>
            {full && <p className={classes.editorHint}>هر {faNumber(MAX_SHORTCUTS)} جا پر است. برای افزودن، اول یکی از میانبرهای بالا را حذف کنید.</p>}
            <div className={classes.card}>
              {rest.map((section) => {
                const Icon = section.icon;
                return (
                  <div key={section.id} className={classes.editorRow}>
                    <span className={`${classes.editorIcon} ${classes.tone}`} data-tone={section.group}>
                      <Icon size={22} stroke={1.7} aria-hidden />
                    </span>
                    <span className={classes.editorLabel}>
                      <strong>{section.label}</strong>
                      <span>{groupLabel(section)}</span>
                    </span>
                    <ActionIcon variant="subtle" color="teal" size={44} radius="xl" disabled={full} onClick={() => add(section.id)} aria-label={`افزودن «${section.label}» به میانبرها`}>
                      <IconCirclePlus size={22} stroke={1.7} />
                    </ActionIcon>
                  </div>
                );
              })}
            </div>
          </Stack>
        )}

        <Group grow gap="sm" className={classes.editorActions}>
          <Button variant="default" size="md" onClick={() => finish(onReset)}>
            بازگشت به پیش‌فرض
          </Button>
          <Button size="md" onClick={() => finish(() => onSave(draft))}>
            ذخیره
          </Button>
        </Group>
      </Stack>
    </CustomDrawer>
  );
}
